import {announce} from '@github-ui/aria-live'
import {useIssueState} from '@github-ui/issue-pr-state'
import type {IssuePickerItem} from '@github-ui/item-picker/IssuePicker'
import {RepositoryAndIssuePicker} from '@github-ui/item-picker/RepositoryAndIssuePicker'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {
  type Icon,
  IssueClosedIcon,
  IssueDraftIcon,
  IssueOpenedIcon,
  SkipIcon,
  TriangleDownIcon,
} from '@primer/octicons-react'
import {ActionList, ActionMenu, StateLabel} from '@primer/react'
import type React from 'react'
import {useCallback, useRef, useState} from 'react'

import {TEST_IDS} from '../../constants/test-ids'
import {type IssueStatusOption, needsDuplicateIssue, type SetIssueStatus} from '../../mutations/use-set-issue-status'
import {DuplicateIssueLink, HeaderState, type HeaderStateFragment} from './HeaderState'
import styles from './HeaderState.module.css'
import {useIssueHeaderDataContext} from './IssueHeaderDataContext'
import {IssueInProgressIcon} from './IssueInProgressIcon'

export type HeaderStatusMenuProps = {
  isSticky: boolean
  stateData: HeaderStateFragment
  status: IssueStatusOption | null
  statuses: readonly IssueStatusOption[]
  issueId: string
  repositoryNameWithOwner?: string
  owner: string
  repo: string
  setIssueStatus: SetIssueStatus
  viewerCanSetStatus: boolean
}

/** Reads the issue from the header context so HeaderMetadata doesn't have to widen its fragment. */
export function HeaderStatus({
  isSticky,
  setIssueStatus,
  stateData,
}: {
  isSticky: boolean
  setIssueStatus: SetIssueStatus
  stateData: HeaderStateFragment
}) {
  const issue = useIssueHeaderDataContext()
  return (
    <HeaderStatusMenu
      isSticky={isSticky}
      stateData={stateData}
      status={issue.status}
      statuses={issue.repository.owner.issueStatuses?.nodes?.filter(isStatus) ?? []}
      issueId={issue.id}
      repositoryNameWithOwner={issue.repository.nameWithOwner}
      owner={issue.repository.owner.login}
      repo={issue.repository.name}
      setIssueStatus={setIssueStatus}
      viewerCanSetStatus={issue.viewerCanSetStatus}
    />
  )
}

// Keyed by name while statuses are in flux.
type StatusColor = 'open' | 'done' | 'muted'

const STATUS_VISUALS: Record<string, {icon: Icon; color: StatusColor}> = {
  New: {icon: IssueOpenedIcon, color: 'open'},
  Backlog: {icon: IssueDraftIcon, color: 'muted'},
  Todo: {icon: IssueOpenedIcon, color: 'open'},
  'In Progress': {icon: IssueInProgressIcon, color: 'open'},
  Done: {icon: IssueClosedIcon, color: 'done'},
  'Not planned': {icon: SkipIcon, color: 'muted'},
  Duplicate: {icon: SkipIcon, color: 'muted'},
}

const STATUS_ICON_CLASSES: Record<StatusColor, string> = {
  open: styles.statusIconOpen,
  done: styles.statusIconDone,
  muted: styles.statusIconMuted,
}

const STATUS_LABEL_STYLES: Record<StatusColor, React.CSSProperties> = {
  open: {
    backgroundColor: 'var(--bgColor-open-emphasis)',
    boxShadow: 'var(--boxShadow-thin) var(--borderColor-open-emphasis)',
  },
  done: {
    backgroundColor: 'var(--bgColor-done-emphasis)',
    boxShadow: 'var(--boxShadow-thin) var(--borderColor-done-emphasis)',
  },
  muted: {
    backgroundColor: 'var(--bgColor-neutral-emphasis)',
    boxShadow: 'var(--boxShadow-thin) var(--borderColor-neutral-emphasis)',
  },
}

function statusVisual(status: IssueStatusOption): {
  icon: Icon
  iconClassName: string
  labelStyle: React.CSSProperties
} {
  const visual =
    STATUS_VISUALS[status.name] ??
    (status.category.state === 'OPEN'
      ? {icon: IssueOpenedIcon, color: 'open' as const}
      : {icon: IssueClosedIcon, color: 'done' as const})
  return {
    icon: visual.icon,
    iconClassName: STATUS_ICON_CLASSES[visual.color],
    labelStyle: STATUS_LABEL_STYLES[visual.color],
  }
}

function isStatus(status: IssueStatusOption | null): status is IssueStatusOption {
  return status !== null
}

function fitsState(status: IssueStatusOption, stateData: HeaderStateFragment): boolean {
  if (status.category.state !== stateData.state) return false
  return status.category.state === 'OPEN' || status.category.stateReason === (stateData.stateReason ?? 'COMPLETED')
}

// The state can change without the status, like closing through the legacy close button, so a status
// only counts while it fits the state. Otherwise a closed issue falls back to the first status for its reason.
function currentStatus(
  stateData: HeaderStateFragment,
  statuses: readonly IssueStatusOption[],
  ...candidates: Array<IssueStatusOption | null>
): IssueStatusOption | undefined {
  const fitting = candidates.find(candidate => candidate && fitsState(candidate, stateData))
  if (fitting) return fitting
  if (stateData.state !== 'CLOSED') return undefined
  return statuses.find(status => fitsState(status, stateData))
}

export function HeaderStatusMenu({
  isSticky,
  stateData,
  status: savedStatus,
  statuses,
  issueId,
  repositoryNameWithOwner,
  owner,
  repo,
  setIssueStatus,
  viewerCanSetStatus,
}: HeaderStatusMenuProps) {
  const {addToast} = useToastContext()
  const {stateString, stateStatus} = useIssueState({
    state: stateData.state,
    stateReason: stateData.stateReason,
    options: {longText: !isSticky},
  })
  const [selected, setSelected] = useState<IssueStatusOption | null>(null)
  // Drop the local pick once the saved status changes, whether it's confirmed or someone else set one.
  const [savedStatusId, setSavedStatusId] = useState(savedStatus?.id)
  if (savedStatus?.id !== savedStatusId) {
    setSavedStatusId(savedStatus?.id)
    setSelected(null)
  }
  const [open, setOpen] = useState(false)
  const [pickerType, setPickerType] = useState<'Issue' | 'Repository' | null>(null)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const status = currentStatus(stateData, statuses, selected, savedStatus)
  const selectableStatuses = statuses.filter(option => option.name !== 'New')
  const visual = status ? statusVisual(status) : undefined
  const LabelIcon = visual?.icon
  const statusLabel = status?.name ?? stateString
  // `StateLabel` picks its own octicon from the lifecycle state, and `open` and `closed` are the two
  // statuses it renders without one, leaving the granular icon to stand on its own.
  const iconlessStatus = stateData.state === 'CLOSED' ? 'closed' : 'open'

  const submit = useCallback(
    (option: IssueStatusOption, duplicateIssue?: IssuePickerItem) => {
      const [duplicateOwner = '', duplicateRepo = ''] = duplicateIssue?.repository.nameWithOwner.split('/') ?? []
      setSelected(option)
      setIssueStatus(
        {
          status: option,
          currentState: stateData.state === 'CLOSED' ? 'CLOSED' : 'OPEN',
          currentStateReason: stateData.stateReason ?? null,
          duplicateIssue: duplicateIssue && {
            id: duplicateIssue.id,
            number: duplicateIssue.number,
            url: duplicateIssue.url,
            repository: {name: duplicateRepo, owner: {login: duplicateOwner}},
          },
        },
        {
          onCompleted: () => announce(`Status set to ${option.name}`),
          onError: () => {
            setSelected(current => (current?.id === option.id ? null : current))
            announce(`Could not set the status to ${option.name}`, {assertive: true})
            // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
            addToast({type: 'error', message: `Could not set the status to ${option.name}`})
          },
        },
      )
    },
    [addToast, setIssueStatus, stateData.state, stateData.stateReason],
  )

  const [duplicateStatus, setDuplicateStatus] = useState<IssueStatusOption | null>(null)
  const onSelect = (option: IssueStatusOption) => {
    if (needsDuplicateIssue(option)) {
      setDuplicateStatus(option)
      setPickerType('Issue')
      return
    }
    if (option.id !== status?.id) submit(option)
  }

  // Viewers who can't close or reopen the issue see the plain label.
  // No statuses means they're off for this owner, even if the client flag is on.
  if (!viewerCanSetStatus || selectableStatuses.length === 0) {
    return <HeaderState isSticky={isSticky} stateData={stateData} repositoryNameWithOwner={repositoryNameWithOwner} />
  }

  return (
    <div className={styles.statusContainer}>
      <ActionMenu anchorRef={anchorRef} open={open} onOpenChange={setOpen}>
        <ActionMenu.Anchor>
          <button type="button" className={styles.statusButton} aria-label={`Status: ${statusLabel}`}>
            <StateLabel
              data-testid={TEST_IDS.headerState}
              status={visual ? iconlessStatus : stateStatus}
              className={styles.stateLabel}
              style={visual?.labelStyle}
            >
              {LabelIcon && <LabelIcon className={styles.statusLabelIcon} />}
              <span>{statusLabel}</span>
              <TriangleDownIcon className={styles.statusCaret} />
            </StateLabel>
          </button>
        </ActionMenu.Anchor>
        <ActionMenu.Overlay width="small">
          <ActionList selectionVariant="single" showDividers={false}>
            {selectableStatuses.map(option => {
              const {icon: StatusIcon, iconClassName} = statusVisual(option)
              return (
                <ActionList.Item key={option.id} selected={option.id === status?.id} onSelect={() => onSelect(option)}>
                  <ActionList.LeadingVisual>
                    <StatusIcon className={iconClassName} />
                  </ActionList.LeadingVisual>
                  {option.name}
                </ActionList.Item>
              )
            })}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      {status && needsDuplicateIssue(status) && (
        <DuplicateIssueLink
          duplicateOf={stateData.duplicateOf}
          repositoryNameWithOwner={repositoryNameWithOwner}
          insideStateLabel={false}
        />
      )}
      <RepositoryAndIssuePicker
        issuePickerProps={{insidePortal: true, responsiveOnNarrow: true}}
        repositoryPickerProps={{responsiveOnNarrow: true, insidePortal: true, hideContainer: true}}
        selectedIssueIds={[]}
        hiddenIssueIds={[issueId]}
        onPickerTypeChange={setPickerType}
        onIssueSelection={issues => {
          setPickerType(null)
          if (issues[0] && duplicateStatus) submit(duplicateStatus, issues[0])
        }}
        defaultRepositoryNameWithOwner={repositoryNameWithOwner ?? `${owner}/${repo}`}
        organization={owner}
        pickerType={pickerType}
        anchorElement={props => {
          const {ref} = props as React.HTMLAttributes<HTMLButtonElement> & {
            ref: React.RefObject<HTMLButtonElement | null>
          }
          if (ref) ref.current = anchorRef.current
          // The status button above is the anchor, so the picker doesn't render its own.
          return <></>
        }}
      />
    </div>
  )
}
